import {
  nodeWatchView,
  type NodeContainer,
  type NodeWatchRequest,
  type NodeWatchView,
} from '@occ/contracts';
import { del, get, put } from '../api/http';
import { ROUTES } from '../api/routes';
import { runAction } from './actions';
import { listProcesses, type ProcessRow } from './services';

/**
 * Procesos y contenedores vigilados por nodo (M16). Lo vigilado vive como reglas
 * de alerta de ámbito nodo, pero el panel solo ve este estado resumido: igual que
 * con los interruptores, no necesita saber que por debajo hay una `AlertRule`.
 */

export const fetchNodeWatches = async (nodeId: string): Promise<NodeWatchView[]> =>
  nodeWatchView.array().parse(await get(ROUTES.nodeWatches(nodeId)));

export const setNodeWatch = async (
  nodeId: string,
  request: NodeWatchRequest,
): Promise<NodeWatchView> => nodeWatchView.parse(await put(ROUTES.nodeWatches(nodeId), request));

export const removeNodeWatch = (nodeId: string, ruleId: string): Promise<void> =>
  del(ROUTES.nodeWatch(nodeId, ruleId));

export const listContainers = async (nodeId: string): Promise<NodeContainer[]> => {
  const { containers } = await runAction('containers.list', { node: { nodeId } });
  return containers;
};

/** Lo que el selector ofrece vigilar: un nombre, y de dónde sale para enseñarlo. */
export type WatchCandidate = {
  kind: 'process' | 'container';
  /** Lo que se guardaría como objetivo de la vigilancia. */
  target: string;
  /** Texto secundario: PID, imagen, estado... */
  detail: string;
  /** Ya está corriendo ahora mismo. */
  running: boolean;
  /** Un `*` al final alternativo, si el nombre parece cambiar en cada despliegue. */
  suggestedPrefix?: string;
};

/**
 * Los contenedores de Coolify que son aplicaciones se recrean en cada despliegue y
 * el nombre lleva una marca de tiempo al final (`<id>-<dígitos>`), así que vigilar
 * el nombre exacto dejaría la regla apuntando a algo que ya no existe después del
 * primer redespliegue. Se sugiere el prefijo; el operador decide.
 *
 * La heurística exige la etiqueta `coolify.managed` **y** la forma del nombre: un
 * contenedor de Compose cualquiera con un nombre que acabe en dígitos no la cumple.
 * Y no todo lo que corre bajo Coolify cambia de nombre (bases de datos, servicios),
 * por eso es solo una sugerencia, nunca el valor por defecto.
 */
const COOLIFY_RECREATED_NAME = /^(.+-)\d{6,}$/;

export function containerCandidate(container: NodeContainer): WatchCandidate {
  const managed = container.labels?.['coolify.managed'] === 'true';
  const prefix = managed ? COOLIFY_RECREATED_NAME.exec(container.name)?.[1] : undefined;
  return {
    kind: 'container',
    target: container.name,
    detail: `${container.image} · ${container.status}`,
    running: container.state === 'running',
    suggestedPrefix: prefix ? `${prefix}*` : undefined,
  };
}

/**
 * Los procesos se ofrecen **por nombre** y no por PID: el PID cambia en cada
 * reinicio, así que una vigilancia atada a él no distinguiría "se cayó" de "se
 * reinició bien" y nunca se resolvería sola. El PID se enseña como contexto, para
 * que el operador reconozca el proceso que está mirando.
 *
 * Varios procesos con el mismo nombre se agrupan en una fila: se vigila "que haya
 * alguno", que es lo que mide el barrido.
 */
export function processCandidates(processes: ProcessRow[]): WatchCandidate[] {
  const byName = new Map<string, ProcessRow[]>();
  for (const process of processes) {
    // Los protegidos del sistema sin nombre no se pueden vigilar por nombre.
    if (process.name.trim() === '') continue;
    byName.set(process.name, [...(byName.get(process.name) ?? []), process]);
  }
  return [...byName.entries()]
    .map(([name, group]) => ({
      kind: 'process' as const,
      target: name,
      detail:
        group.length === 1
          ? `PID ${group[0]?.pid}`
          : `${group.length} procesos · PID ${group
              .slice(0, 3)
              .map((p) => p.pid)
              .join(', ')}${group.length > 3 ? '…' : ''}`,
      running: true,
    }))
    .sort((a, b) => a.target.localeCompare(b.target, 'es'));
}

export const loadCandidates = async (
  nodeId: string,
  kind: 'process' | 'container',
): Promise<WatchCandidate[]> =>
  kind === 'process'
    ? processCandidates(await listProcesses(nodeId))
    : (await listContainers(nodeId)).map(containerCandidate);
